import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import style from './JobDetailPanel.module.sass';

const JobDetailPanel = ({ job, open, onClose, onJobUpdated }) => {
  const [jobDetail, setJobDetail] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followUpNote, setFollowUpNote] = useState('');
  const [viewMode, setViewMode] = useState('history'); // 'history' or 'notes'

  // Editable notes state
  const [editableNotes, setEditableNotes] = useState({
    job_note: '',
    photographer_note: ''
  });
  const [notesModified, setNotesModified] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Rig/Photographer assignment state
  const [allPhotographers, setAllPhotographers] = useState([]);
  const [rigs, setRigs] = useState([]);
  const [rigsModified, setRigsModified] = useState(false);
  const [savingRigs, setSavingRigs] = useState(false);
  const [rigsToDelete, setRigsToDelete] = useState([]);

  useEffect(() => {
    if (open && job) {
      fetchJobDetails();
      fetchPhotographers();
    }
  }, [open, job?.id]);

  // Initialize editable notes and rigs when job detail loads
  useEffect(() => {
    if (jobDetail) {
      setEditableNotes({
        job_note: jobDetail.job_note || '',
        photographer_note: jobDetail.photographer_note || ''
      });
      setNotesModified(false);

      // Initialize rigs from job detail, or start with 3 empty rows
      const existingRigs = (jobDetail.rigs || []).map(rig => ({
        id: rig.id,
        name: rig.name || '',
        photographer_id: rig.photographer_id || '',
        milage_miles: rig.milage_miles || '',
        milage_vehicle: rig.milage_vehicle || '',
        note: rig.note || '',
        _destroy: false
      }));

      // If no existing rigs, start with 3 empty rows
      if (existingRigs.length === 0) {
        setRigs([
          { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false },
          { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false },
          { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false }
        ]);
      } else {
        setRigs(existingRigs);
      }
      setRigsModified(false);
      setRigsToDelete([]);
    }
  }, [jobDetail]);

  const fetchPhotographers = async () => {
    try {
      const response = await axios.get('/api/v1/calendar_jobs/photographers');
      setAllPhotographers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch photographers:', error);
    }
  };

  const fetchJobDetails = async () => {
    if (!job) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/calendar_jobs/${job.id}`);
      setJobDetail(response.data.job);
      setChanges(response.data.changes || []);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
    }
    setLoading(false);
  };

  const handleNoteChange = (field, value) => {
    setEditableNotes(prev => ({ ...prev, [field]: value }));
    setNotesModified(true);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(`/api/v1/calendar_jobs/${job.id}/update_notes`, {
        job_note: editableNotes.job_note,
        photographer_note: editableNotes.photographer_note
      });
      setNotesModified(false);
      setSavingNotes(false);
      onClose(); // Close the panel after saving
    } catch (error) {
      alert('Failed to save notes: ' + error.message);
      setSavingNotes(false);
    }
  };

  const handleRigChange = (index, field, value) => {
    // Check for duplicate rig name
    if (field === 'name' && value) {
      const existingRig = rigs.find((r, i) => i !== index && !r._destroy && r.name === value);
      if (existingRig) {
        alert(`Rig "${value}" is already assigned to this job.`);
        return;
      }
    }

    // Check for duplicate photographer
    if (field === 'photographer_id' && value) {
      const existingPhotog = rigs.find((r, i) => i !== index && !r._destroy && r.photographer_id == value);
      if (existingPhotog) {
        const photogName = allPhotographers.find(p => p.id == value)?.name || 'This photographer';
        alert(`${photogName} is already assigned to this job.`);
        return;
      }
    }

    setRigs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setRigsModified(true);
  };

  const handleRigDelete = (index) => {
    setRigs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], _destroy: !updated[index]._destroy };
      return updated;
    });
    setRigsModified(true);
  };

  const handleAddRig = () => {
    const rigNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    const usedNames = rigs.filter(r => !r._destroy).map(r => r.name);
    const nextName = rigNames.find(n => !usedNames.includes(n)) || '';

    setRigs(prev => [...prev, {
      id: null,
      name: nextName,
      photographer_id: '',
      milage_miles: '',
      milage_vehicle: '',
      note: '',
      _destroy: false
    }]);
    setRigsModified(true);
  };

  const handleSaveRigs = async () => {
    setSavingRigs(true);
    try {
      await axios.patch(`/api/v1/calendar_jobs/${job.id}/update_rigs`, {
        rigs: rigs.map(rig => ({
          id: rig.id,
          name: rig.name,
          photographer_id: rig.photographer_id || null,
          milage_miles: rig.milage_miles,
          milage_vehicle: rig.milage_vehicle,
          note: rig.note,
          _destroy: rig._destroy
        }))
      });
      setRigsModified(false);
      fetchJobDetails(); // Refresh to get updated rigs
    } catch (error) {
      alert('Failed to save rigs: ' + error.message);
    }
    setSavingRigs(false);
  };

  const handleAddNote = async () => {
    if (!changes.length || !followUpNote.trim()) return;
    const latestChange = changes[0]; // Get the most recent change (first in desc order)

    try {
      await axios.patch(`/api/v1/calendar_jobs/${job.id}/update_status`, {
        change_id: latestChange.id,
        action_type: 'add_note',
        follow_up_notes: followUpNote.trim()
      });
      setFollowUpNote('');
      if (onJobUpdated) {
        onJobUpdated(); // Refresh calendar
      }
      onClose(); // Close the panel after saving
    } catch (error) {
      alert('Failed to add note: ' + error.message);
    }
  };

  const handleUpdateStatus = async (changeId, actionType) => {
    try {
      await axios.patch(`/api/v1/calendar_jobs/${job.id}/update_status`, {
        change_id: changeId,
        action_type: actionType
      });
      fetchJobDetails();
      if (onJobUpdated) {
        onJobUpdated(); // Refresh calendar to update text colors
      }
    } catch (error) {
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'MMMM d, yyyy');
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: style.statusPending,
      emailed: style.statusEmailed,
      confirmed: style.statusConfirmed,
      cancelled: style.statusCancelled,
      rejected: style.statusRejected
    };
    return statusMap[status] || style.statusPending;
  };

  if (!open) return null;

  return (
    <div className={style.panel}>
      <div className={style.header}>
        <h2>Job Details</h2>
        <button className={style.closeButton} onClick={onClose}>
          &times;
        </button>
      </div>

      {loading ? (
        <div className={style.loading}>Loading...</div>
      ) : (
        <div className={style.content}>
          {/* Job Info Section */}
          <section className={style.section}>
            <div className={style.infoGrid}>
              <div className={style.infoItem}>
                <span className={style.label}>School:</span>
                <span className={style.value}>{jobDetail?.school_name || job?.school_name}</span>
              </div>
              <div className={style.infoItem}>
                <span className={style.label}>Date:</span>
                <span className={style.value}>
                  {formatDateOnly(jobDetail?.date || job?.date)}
                </span>
              </div>
              <div className={style.infoItem}>
                <span className={style.label}>Trigs:</span>
                <span className={style.value}>{jobDetail?.trigs || job?.trigs || 0}</span>
              </div>
              <div className={style.infoItem}>
                <span className={style.label}>Job:</span>
                <span className={style.value}>{jobDetail?.job || job?.job || '-'}</span>
              </div>
              {(jobDetail?.principal_name || jobDetail?.principal_email) && (
                <div className={style.infoItem}>
                  <span className={style.label}>Principal:</span>
                  <span className={style.value}>
                    {jobDetail.principal_name}
                    {jobDetail.principal_email && (
                      <> <a href={`mailto:${jobDetail.principal_email}`}>{jobDetail.principal_email}</a></>
                    )}
                  </span>
                </div>
              )}
              {(jobDetail?.sec1_name || jobDetail?.sec1_email) && (
                <div className={style.infoItem}>
                  <span className={style.label}>Secretary:</span>
                  <span className={style.value}>
                    {jobDetail.sec1_name}
                    {jobDetail.sec1_email && (
                      <> <a href={`mailto:${jobDetail.sec1_email}`}>{jobDetail.sec1_email}</a></>
                    )}
                  </span>
                </div>
              )}
              {jobDetail?.school_phone && (
                <div className={style.infoItem}>
                  <span className={style.label}>Phone:</span>
                  <span className={style.value}>
                    <a href={`tel:${jobDetail.school_phone}`}>{jobDetail.school_phone}</a>
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Actions Section */}
          <section className={style.section}>
            <h3>Actions</h3>
            <div className={style.actions}>
              <button
                className={`${style.actionButton} ${viewMode === 'notes' ? style.activeButton : ''}`}
                onClick={() => setViewMode('notes')}
              >
                Notes
              </button>
              <button
                className={style.actionButton}
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('job_id', job?.id);
                  const returnUrl = `${window.location.pathname}?${params.toString()}`;
                  window.open(`/djobs/${job?.id}/edit?type=confirms&return_to=${encodeURIComponent(returnUrl)}`, '_blank');
                }}
              >
                Job
              </button>
              <button
                className={style.actionButton}
                onClick={() => window.open(`/admin/schools/${jobDetail?.school_id || job?.school_id}/edit?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`, '_blank')}
              >
                School
              </button>
              <button
                className={`${style.actionButton} ${viewMode === 'history' ? style.activeButton : ''}`}
                onClick={() => setViewMode('history')}
              >
                History
              </button>
            </div>
          </section>

          {/* Notes Section (Editable) */}
          {viewMode === 'notes' && (
            <section className={style.section}>
              <div className={style.jobNotesSection}>
                <div className={style.noteField}>
                  <label className={style.noteLabel}>Job Note:</label>
                  <textarea
                    className={style.noteTextarea}
                    value={editableNotes.job_note}
                    onChange={(e) => handleNoteChange('job_note', e.target.value)}
                    placeholder="Enter job note..."
                    rows={3}
                  />
                </div>
                <div className={style.noteField}>
                  <label className={style.noteLabel}>Photographer Note:</label>
                  <textarea
                    className={style.noteTextarea}
                    value={editableNotes.photographer_note}
                    onChange={(e) => handleNoteChange('photographer_note', e.target.value)}
                    placeholder="Enter photographer note..."
                    rows={3}
                  />
                </div>
                {notesModified && (
                  <div className={style.noteSaveButtons}>
                    <button
                      className={style.saveNotesButton}
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                    >
                      {savingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    <button
                      className={style.cancelButton}
                      onClick={() => {
                        setEditableNotes({
                          job_note: jobDetail?.job_note || '',
                          photographer_note: jobDetail?.photographer_note || ''
                        });
                        setNotesModified(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* History Section */}
          {viewMode === 'history' && (
            <section className={style.section}>
              <h3>History</h3>
              {jobDetail?.confirm_status && (
                <div className={style.confNote}>
                  <strong>Status:</strong> {jobDetail.confirm_status}
                </div>
              )}
              {jobDetail?.conf_note && (
                <div className={style.confNote}>
                  <strong>Confirmed By:</strong> {jobDetail.conf_note}
                </div>
              )}
              {changes.length === 0 && !jobDetail?.conf_note && !jobDetail?.confirm_status ? (
                <p className={style.noChanges}>No history recorded.</p>
              ) : changes.length === 0 ? null : (
                <div className={style.changesList}>
                  {changes.map((change) => (
                    <div key={change.id} className={style.changeItem}>
                      <div className={style.changeHeader}>
                        <span className={`${style.statusBadge} ${getStatusBadgeClass(change.status)}`}>
                          {change.status}
                        </span>
                        <span className={style.changeDate}>
                          {formatDateTime(change.created_at)}
                        </span>
                      </div>
                      <div className={style.changeDates}>
                        <span>{format(new Date(change.old_date), 'MMM d')} &rarr; </span>
                        <strong>{format(new Date(change.new_date), 'MMM d, yyyy')}</strong>
                      </div>
                      <div className={style.changeReason}>
                        <strong>Reason:</strong> {change.reason}
                      </div>
                      <div className={style.changedBy}>
                        By: {change.changed_by}
                      </div>
                      {change.follow_up_notes && (
                        <div className={style.followUpNotes}>
                          <strong>Notes:</strong> {change.follow_up_notes}
                        </div>
                      )}
                      {change.emailed_at && (
                        <div className={style.timestamp}>
                          Emailed: {formatDateTime(change.emailed_at)}
                        </div>
                      )}
                      {change.confirmed_at && (
                        <div className={style.timestamp}>
                          Confirmed: {formatDateTime(change.confirmed_at)}
                        </div>
                      )}
                      {change.status !== 'confirmed' && change.status !== 'rejected' && (
                        <div className={style.changeActions}>
                          {change.status === 'pending' && (
                            <button
                              className={style.emailButton}
                              onClick={() => handleUpdateStatus(change.id, 'email')}
                            >
                              Mark Emailed
                            </button>
                          )}
                          <button
                            className={style.confirmButton}
                            onClick={() => handleUpdateStatus(change.id, 'confirm')}
                          >
                            Mark Confirmed
                          </button>
                          <button
                            className={style.rejectButton}
                            onClick={() => handleUpdateStatus(change.id, 'reject')}
                          >
                            Mark Rejected
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Reconfirm Note Form - always visible in History */}
              <div className={style.reconfirmNoteSection}>
                <textarea
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                  placeholder="Enter reconfirm note..."
                  rows={4}
                  className={style.noteTextarea}
                />
                <div className={style.noteFormButtons}>
                  <button
                    className={style.saveNotesButton}
                    onClick={handleAddNote}
                    disabled={!followUpNote.trim()}
                  >
                    Save
                  </button>
                  <button
                    className={style.cancelButton}
                    onClick={() => setFollowUpNote('')}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Rig/Photographer Assignment Section - only show on Notes view */}
          {viewMode === 'notes' && (
          <section className={style.section}>
            <h3>Rigs / Photographers</h3>
            <div className={style.rigTable}>
              <div className={style.rigHeader}>
                <span className={style.rigColName}>Rig</span>
                <span className={style.rigColPhotog}>Photographer</span>
                <span className={style.rigColMiles}>Miles</span>
                <span className={style.rigColVehicle}>Vehicle</span>
                <span className={style.rigColNote}>Note</span>
                <span className={style.rigColDelete}>Del</span>
              </div>
              {rigs.map((rig, index) => (
                <div key={rig.id || `new-${index}`} className={`${style.rigRow} ${rig._destroy ? style.rigRowDeleted : ''}`}>
                  <select
                    className={style.rigColName}
                    value={rig.name}
                    onChange={(e) => handleRigChange(index, 'name', e.target.value)}
                  >
                    <option value="">-</option>
                    {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <select
                    className={style.rigColPhotog}
                    value={rig.photographer_id || ''}
                    onChange={(e) => handleRigChange(index, 'photographer_id', e.target.value)}
                  >
                    <option value="">Select...</option>
                    {allPhotographers.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className={style.rigColMiles}
                    value={rig.milage_miles || ''}
                    onChange={(e) => handleRigChange(index, 'milage_miles', e.target.value)}
                  />
                  <input
                    type="text"
                    className={style.rigColVehicle}
                    value={rig.milage_vehicle || ''}
                    onChange={(e) => handleRigChange(index, 'milage_vehicle', e.target.value)}
                  />
                  <input
                    type="text"
                    className={style.rigColNote}
                    value={rig.note || ''}
                    onChange={(e) => handleRigChange(index, 'note', e.target.value)}
                  />
                  <input
                    type="checkbox"
                    className={style.rigColDelete}
                    checked={rig._destroy}
                    onChange={() => handleRigDelete(index)}
                  />
                </div>
              ))}
            </div>
            <button className={style.addRigButton} onClick={handleAddRig}>
              Add Rig/Photographer
            </button>
            {rigsModified && (
              <div className={style.noteSaveButtons}>
                <button
                  className={style.saveNotesButton}
                  onClick={handleSaveRigs}
                  disabled={savingRigs}
                >
                  {savingRigs ? 'Saving...' : 'Save Rigs'}
                </button>
                <button
                  className={style.cancelButton}
                  onClick={() => {
                    const existingRigs = (jobDetail?.rigs || []).map(rig => ({
                      id: rig.id,
                      name: rig.name || '',
                      photographer_id: rig.photographer_id || '',
                      milage_miles: rig.milage_miles || '',
                      milage_vehicle: rig.milage_vehicle || '',
                      note: rig.note || '',
                      _destroy: false
                    }));
                    if (existingRigs.length === 0) {
                      setRigs([
                        { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false },
                        { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false },
                        { id: null, name: '', photographer_id: '', milage_miles: '', milage_vehicle: '', note: '', _destroy: false }
                      ]);
                    } else {
                      setRigs(existingRigs);
                    }
                    setRigsModified(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </section>
          )}
        </div>
      )}
    </div>
  );
};

export default JobDetailPanel;
